import { GroupFilterElement } from './GroupFilterElement';
import { Filter, FilterGroup, GroupedFilter } from './filter-types';
import { useFilterQueryMode } from './useFilterQueryMode';
import { useFilterStateMode } from './useFilterStateMode';
import { FilterButton } from './FilterButton';
import { FilterElement } from './FilterElement';

export const useGroupedFilters = ({
  filters,
  groups,
  mode = 'query',
  display = 'col',
}: {
  filters: Filter[];
  groups: FilterGroup[];
  mode?: 'query' | 'state';
  display?: 'row' | 'col';
}) => {
  const useFilterMode =
    mode === 'query' ? useFilterQueryMode : useFilterStateMode;

  const {
    filterValues,
    clearCounts,
    setFilterValue,
    applyFilterWrites,
    visibleFilters,
    setVisibleFilters,
    removeFilter,
    setFilterValues,
  } = useFilterMode({
    filters: filters as Filter[],
  });

  // Segregate group filters
  const groupMap = groups.reduce(
    (map: Record<string, string>, group: FilterGroup) => {
      map[group.id] = group.label;

      return map;
    },
    {},
  );

  const groupedFilters: GroupedFilter[] = [];
  const ungroupedFilters: Filter[] = [];

  for (const filter of filters) {
    if (!visibleFilters.includes(filter.id)) {
      continue;
    }
    if (filter.groupId) {
      const group = groupedFilters.find((group) => group.id === filter.groupId);
      if (group) {
        group.elements.push(filter);
      } else {
        groupedFilters.push({
          id: filter.groupId,
          groupLabel: groupMap[filter.groupId],
          elements: [filter],
        });
      }
    } else {
      ungroupedFilters.push(filter);
    }
  }

  const genericFiltersRender = ungroupedFilters.map((filter) => {
    return (
      <FilterElement
        key={filter.id}
        filter={filter}
        filters={filters}
        mode={mode}
        filterValues={filterValues}
        clearCounts={clearCounts}
        setFilterValue={setFilterValue}
        removeFilter={removeFilter}
      />
    );
  });

  const groupedFiltersRender = groupedFilters.map((filter) => {
    return (
      <GroupFilterElement
        key={filter.id}
        filter={filter}
        filterValues={filterValues}
        mode={mode}
        visibleFilters={visibleFilters}
        allFilters={filters}
        setFilterValue={setFilterValue}
        removeFilter={removeFilter}
        display={display}
      />
    );
  });

  return {
    filterButton: (
      <FilterButton
        options={filters.map((filter) => ({
          value: filter.id,
          label: (filter as Filter).label,
          permanent: (filter as Filter).permanent,
        }))}
        filters={visibleFilters}
        setFilters={setVisibleFilters}
      />
    ),
    filtersRender: {
      grouped: groupedFiltersRender,
      generic: genericFiltersRender,
    },
    visibleFilters,
    filterValues,
    setFilterValue,
    applyFilterWrites,
    setVisibleFilters,
    setFilterValues,
  };
};
